import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import {
    BlockVariant,
    BlockVariantBaseProps,
    BlockVariantProps,
    BlockVariantSide,
    getVariantCss,
    variantDefaultProps,
} from "@tKgpco/Components/Common/Block/variant";
import { COMMON, TYPOGRAPHY } from "@tKgpco/theme/design";
import { siteWidthStyles } from "@tKgpco/theme/mixins";
import breakpointValuesMediaQueries from "@tKgpco/utilities/breakpointValuesMediaQueries";
import React from "react";
import styled, { css } from "styled-components";

// -- INTERFACES

export interface BlockBorderProps {
    width: string;
    type: string;
    color: string;
}

export interface BlockBaseProps {
    fullWidth?: boolean;
    variant?: BlockVariant;
    variantProps?: BlockVariantProps;
    variantSide?: BlockVariantSide;
    margin?: Array<string | null>;
    preserveSpacing?: boolean;
}

export interface BlockProps extends BlockBaseProps, BlockVariantBaseProps {}

// -- UTILS

const parseProps = ({
    fullWidth,
    variant,
    variantProps,
    variantSide,
    margin,
    preserveSpacing,
    containerCss,
    spacerCss,
    wrapperCss,
    ...otherProps
}: Partial<BlockProps>) => {
    const blockProps: BlockProps = {
        fullWidth,
        variant,
        variantProps,
        variantSide,
        margin,
        preserveSpacing,
        containerCss,
        spacerCss,
        wrapperCss,
    };

    return { blockProps, otherProps };
};

// -- COMPONENTS

export const BlockWrapper = styled.div<BlockProps>`
    ${props => props.wrapperCss}
    ${getVariantCss("wrapperCss")}
`;

export const BlockSpacer = styled.div<BlockProps>`
    ${siteWidthStyles()}

    ${props =>
        breakpointValuesMediaQueries(
            props.theme,
            [{ values: props.margin ?? [], properties: ["margin-top", "margin-bottom"] }],
            mediaOptions.min,
        )}

    ${props =>
        props.fullWidth
            ? css`
                  max-width: none;
              `
            : null}
    ${props => props.spacerCss}
    ${getVariantCss("spacerCss")}
`;

const BlockContainerBase = styled.div<BlockProps>``;
export const BlockContainer = styled(BlockContainerBase)`
    ${({ preserveSpacing }) => {
        return css`
            ${BlockContainerBase} & {
                > ${BlockSpacer} {
                    ${!preserveSpacing
                        ? css`
                              margin-top: ${TYPOGRAPHY.spacing.base};
                              margin-bottom: ${TYPOGRAPHY.spacing.base};
                          `
                        : null}
                    padding-left: 0;
                    padding-right: 0;
                }

                &:first-child > ${BlockSpacer} {
                    margin-top: 0;
                }
                &:last-child > ${BlockSpacer} {
                    margin-bottom: 0;
                }
            }
        `;
    }}

    ${props => props.containerCss}
    ${getVariantCss("containerCss")}
`;

const Block: React.FC<BlockProps & React.HTMLAttributes<HTMLDivElement>> = ({ children, ...props }) => {
    if (!React.Children.count(children)) {
        return null;
    }

    const { blockProps, otherProps } = parseProps(props);

    return (
        <BlockContainer {...otherProps} {...blockProps}>
            <BlockSpacer {...blockProps}>
                <BlockWrapper {...blockProps}>{children}</BlockWrapper>
            </BlockSpacer>
        </BlockContainer>
    );
};

Block.displayName = "Block";
(Block as any).defaultProps = {
    margin: COMMON.spacing.content,
    variantProps: variantDefaultProps,
};

export default Block;
